/**
 * Two-Factor Authentication API services
 */
import { api } from './api';
import type { ApiResponse } from '@/types';

// Types for 2FA operations
export interface TwoFactorSetupData {
  secret: string;
  qrCodeUrl: string;
  manualEntryKey: string;
}

export interface TwoFactorSetupResult {
  enabled: boolean;
  backupCodes: string[];
}

export interface TwoFactorVerifyResult {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
}

export interface BackupCodesResult {
  backupCodes: string[];
  generatedAt: string;
}

export interface TwoFactorStatus {
  enabled: boolean;
  enabledAt?: string;
  backupCodesRemaining?: number;
}

// Two-Factor Authentication API
export const twoFactorApi = {
  /**
   * Get current 2FA status for the authenticated user
   */
  getStatus: async (): Promise<ApiResponse<TwoFactorStatus>> => {
    return api.get<TwoFactorStatus>('/v1/auth/two-factor/status');
  },

  /**
   * Begin 2FA setup process
   * Returns QR code URL and manual entry key
   */
  startSetup: async (): Promise<ApiResponse<TwoFactorSetupData>> => {
    return api.post<TwoFactorSetupData>('/v1/auth/two-factor/setup');
  },

  /**
   * Verify 2FA setup with initial TOTP code
   * This enables 2FA on the account and returns backup codes
   */
  verifySetup: async (code: string): Promise<ApiResponse<TwoFactorSetupResult>> => {
    return api.post<TwoFactorSetupResult>('/v1/auth/two-factor/verify-setup', { code });
  },

  /**
   * Verify TOTP code during login
   * This is called after primary authentication when 2FA is enabled
   */
  verify: async (code: string, rememberDevice?: boolean): Promise<ApiResponse<TwoFactorVerifyResult>> => {
    return api.post<TwoFactorVerifyResult>('/v1/auth/two-factor/verify', {
      code,
      rememberDevice: rememberDevice ?? false
    });
  },

  /**
   * Verify using a backup code during login
   * Backup codes are single-use
   */
  verifyBackup: async (backupCode: string): Promise<ApiResponse<TwoFactorVerifyResult>> => {
    return api.post<TwoFactorVerifyResult>('/v1/auth/two-factor/verify-backup', { backupCode });
  },

  /**
   * Disable 2FA on the account
   * Requires password confirmation for security
   */
  disable: async (password: string): Promise<ApiResponse<{ disabled: boolean }>> => {
    return api.post<{ disabled: boolean }>('/v1/auth/two-factor/disable', { password });
  },

  /**
   * Generate new backup codes
   * This invalidates any existing backup codes
   */
  getBackupCodes: async (): Promise<ApiResponse<BackupCodesResult>> => {
    return api.post<BackupCodesResult>('/v1/auth/two-factor/backup-codes');
  },

  /**
   * Regenerate backup codes with password confirmation
   */
  regenerateBackupCodes: async (password: string): Promise<ApiResponse<BackupCodesResult>> => {
    return api.post<BackupCodesResult>('/v1/auth/two-factor/backup-codes/regenerate', { password });
  },
};
